/**
 * Confirms permanent deletion of a schedule scope block and all linked dispatch rows.
 */
import { ModalWrapper } from "../../../../components/ui";
import { Button } from "../../../../components/ui";
import { formatShortMonthDay } from "../../../../utils/datetime/core";

/**
 * Confirm permanent deletion of a schedule scope block and all linked dispatch rows.
 *
 * @param {Object} props
 * @param {boolean} props.open
 * @param {{ scope?: { name?: string }, job?: { number?: string|number, description?: string }, startDate?: Date, endDate?: Date }|null} props.block
 * @param {boolean} [props.busy]
 * @param {() => void} props.onClose
 * @param {() => void|Promise<void>} props.onConfirm
 * @returns {JSX.Element}
 */
export default function ScheduleDeleteScopeBlockModal({
  open,
  block,
  busy = false,
  onClose,
  onConfirm,
}) {
  const scopeName = block?.scope?.name ?? "this scope";
  const jobLabel =
    block?.job?.number != null
      ? `${block.job.number}${block.job.description ? ` — ${block.job.description}` : ""}`
      : "";
  const spanLabel =
    block?.startDate && block?.endDate
      ? `${formatShortMonthDay(block.startDate)} – ${formatShortMonthDay(block.endDate)}`
      : "";

  return (
    <ModalWrapper
      isOpen={open}
      onClose={onClose}
      closeButton={!busy}
      maxWidth={440}
      overlayClassName="z-[10060]"
    >
      <div role="dialog" aria-modal="true" aria-labelledby="schedule-delete-scope-title">
        <div className="border-b border-gray-200 px-4 py-3">
          <h2
            id="schedule-delete-scope-title"
            className="text-base font-semibold text-red-800"
          >
            Delete scope block?
          </h2>
          <p className="mt-1 text-sm text-gray-700">
            <span className="font-medium">{scopeName}</span>
            {jobLabel ? (
              <>
                {" "}
                <span className="text-gray-500">· {jobLabel}</span>
              </>
            ) : null}
            {spanLabel ? (
              <>
                <br />
                <span className="text-gray-600">{spanLabel}</span>
              </>
            ) : null}
          </p>
        </div>
        <div className="space-y-3 px-4 py-4 text-sm text-gray-800">
          <p>
            This removes the <strong>schedule block</strong> permanently. That cannot be undone.
          </p>
          <div className="rounded border border-gray-300 bg-gray-100 px-3 py-2">
            <span>
              <span className="font-bold text-red-800">Warning:</span>
              <span className="mt-1 block text-gray-600">
                This action will delete <strong>ALL schedule and dispatch data</strong> in
                this block, including worker assignments, materials, notes, and other
                per-day scope details.
              </span>
            </span>
          </div>
        </div>
        <div className="px-4 py-3">
          <div className="flex flex-wrap items-center justify-end gap-2 border-t border-gray-100 pt-4">
            <Button
              variant="custom"
              className="border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-800 hover:bg-gray-100"
              onClick={onClose}
              disabled={busy}
              text="Cancel"
            />
            <Button
              variant="custom"
              className="bg-red-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-800"
              onClick={() => void onConfirm()}
              disabled={busy}
              text={busy ? "Deleting…" : "Delete permanently"}
            />
          </div>
        </div>
      </div>
    </ModalWrapper>
  );
}
